// /r/:token — read-only results behind an admin-issued share link.
// English-only, like the admin view it is shared from.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Card, CardBody } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BigFiveChart } from "@/components/bigfive-chart";
import { Attribution } from "@/components/attribution";
import { FacetReport } from "@/components/facet-report";
import { Page } from "@/components/page";
import { ThemeToggle } from "@/components/theme-toggle";
import { api, ApiError, type SharedResultsResponse } from "@/lib/api";

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "error" }
  | { kind: "ok"; data: SharedResultsResponse };

export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    api<SharedResultsResponse>(`/api/share/${encodeURIComponent(token ?? "")}`)
      .then((data) => {
        if (!cancelled) setState({ kind: "ok", data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ kind: err instanceof ApiError && err.status === 404 ? "invalid" : "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const topBar = (
    <div className="mb-6 flex items-center justify-between">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink-muted">
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        AGP · Big Five
      </p>
      <ThemeToggle />
    </div>
  );

  if (state.kind === "loading") {
    return (
      <Page>
        {topBar}
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-md" />
          <Skeleton className="h-72 w-full rounded-md" />
        </div>
      </Page>
    );
  }

  if (state.kind !== "ok") {
    return (
      <Page>
        {topBar}
        <Card>
          <CardBody className="space-y-2 py-8 text-center">
            <h1 className="font-serif text-2xl">
              {state.kind === "invalid" ? "This link is not valid." : "Could not load the results."}
            </h1>
            <p className="text-sm text-ink-muted">
              {state.kind === "invalid"
                ? "It may have expired or been revoked. Ask the person who shared it for a new one."
                : "Please reload the page and try again."}
            </p>
          </CardBody>
        </Card>
      </Page>
    );
  }

  const { data } = state;
  return (
    <Page>
      {topBar}
      <header className="mb-6">
        <h1 className="font-serif text-3xl">{data.email}</h1>
        <p className="text-ink-muted mt-2 text-sm">
          Completed {new Date(data.completedAt).toLocaleDateString()} · Link expires{" "}
          {new Date(data.expiresAt).toLocaleString()}
        </p>
      </header>
      <BigFiveChart scores={data.results.percentiles} lang="en" />
      <Card className="mt-5">
        <CardBody>
          <p className="text-sm text-ink-soft leading-relaxed">{data.results.summary}</p>
        </CardBody>
      </Card>
      <FacetReport facets={data.results.facets} lang="en" />
      <Attribution lang="en" />
    </Page>
  );
}
